import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { Combobox } from '@/components/combobox'
import { MultiCombobox } from '@/components/multi-combobox'
import { notifyError } from '@/components/notify'
import { TimezoneCombobox } from '@/components/timezone-combobox'
import { Button } from '@/components/ui/button'
import { Field, FormError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useGroupOptions, useLocationComboOptions, useTagOptions } from '@/features/groups/components/pickers'
import { applyServerErrors } from '@/lib/form-errors'
import { browserTimeZone } from '@/lib/timezones'
import { emptyToNull } from '@/lib/utils'
import type { Display, Orientation } from '@/types/api'
import { useCreateDisplay, useUpdateDisplay, type DisplayInput } from '../api'
import { ORIENTATION_LABELS } from '../lib/status'

const dimension = z
  .string()
  .trim()
  .refine((v) => v === '' || (/^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= 16384), 'Between 1 and 16384')

export const displaySchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(120),
  location_id: z.string().nullable(),
  description: z.string().max(2000),
  inherit_timezone: z.boolean(),
  timezone: z.string().nullable(),
  orientation: z.enum(['landscape', 'portrait', 'landscape_flipped', 'portrait_flipped']),
  resolution_width: dimension,
  resolution_height: dimension,
  group_ids: z.array(z.string()),
  tag_ids: z.array(z.string()),
})
export type DisplayFormValues = z.infer<typeof displaySchema>

const FIELDS = Object.keys(displaySchema.shape)

export function toDisplayFormValues(display?: Display, defaults: { location_id?: string | null } = {}): DisplayFormValues {
  return {
    name: display?.name ?? '',
    location_id: display ? (display.location?.id ?? null) : (defaults.location_id ?? null),
    description: display?.description ?? '',
    inherit_timezone: display ? display.timezone === null : true,
    timezone: display?.timezone ?? null,
    orientation: display?.orientation ?? 'landscape',
    resolution_width: display?.resolution?.width.toString() ?? '',
    resolution_height: display?.resolution?.height.toString() ?? '',
    group_ids: display?.groups.map((g) => g.id) ?? [],
    tag_ids: display?.tags.map((t) => t.id) ?? [],
  }
}

export function toDisplayPayload(values: DisplayFormValues): DisplayInput {
  return {
    name: values.name.trim(),
    location_id: values.location_id,
    description: emptyToNull(values.description),
    timezone: values.inherit_timezone ? null : values.timezone,
    orientation: values.orientation,
    resolution_width: values.resolution_width ? Number(values.resolution_width) : null,
    resolution_height: values.resolution_height ? Number(values.resolution_height) : null,
    group_ids: values.group_ids,
    tag_ids: values.tag_ids,
  }
}

export function OrientationSelect({ value, onChange, id }: { value: Orientation; onChange: (v: Orientation) => void; id?: string }) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as Orientation)}>
      <SelectTrigger id={id}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {(Object.keys(ORIENTATION_LABELS) as Orientation[]).map((o) => (
          <SelectItem key={o} value={o}>
            {ORIENTATION_LABELS[o]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** Timezone with an "inherit" switch (null → location / organization timezone). */
export function InheritableTimezone({
  inherit,
  onInheritChange,
  value,
  onChange,
  inheritedLabel,
}: {
  inherit: boolean
  onInheritChange: (v: boolean) => void
  value: string | null
  onChange: (v: string) => void
  inheritedLabel: string
}) {
  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2">
        <div className="min-w-0">
          <Label htmlFor="tz-inherit" className="text-[13px]">
            Inherit timezone
          </Label>
          <p className="truncate text-xs text-muted-foreground">{inheritedLabel}</p>
        </div>
        <Switch id="tz-inherit" checked={inherit} onCheckedChange={onInheritChange} />
      </div>
      {!inherit && <TimezoneCombobox value={value} onChange={onChange} />}
    </div>
  )
}

export function DisplayForm({
  display,
  defaultLocationId,
  formId,
  hideActions = false,
  compact = false,
  submitLabel,
  onPendingChange,
  onSuccess,
}: {
  display?: Display
  defaultLocationId?: string | null
  formId?: string
  hideActions?: boolean
  /** Onboarding variant: name, location, orientation only. */
  compact?: boolean
  submitLabel?: string
  onPendingChange?: (pending: boolean) => void
  onSuccess: (display: Display) => void
}) {
  const create = useCreateDisplay()
  const update = useUpdateDisplay(display?.id ?? '')
  const locationOptions = useLocationComboOptions()
  const groupOptions = useGroupOptions()
  const tagOptions = useTagOptions()
  const form = useForm<DisplayFormValues>({
    resolver: zodResolver(displaySchema),
    defaultValues: toDisplayFormValues(display, { location_id: defaultLocationId }),
  })
  const { errors, isSubmitting } = form.formState
  const inherit = form.watch('inherit_timezone')
  const locationId = form.watch('location_id')
  const location = locationOptions.find((o) => o.value === locationId)

  const onSubmit = form.handleSubmit(async (values) => {
    if (!values.inherit_timezone && !values.timezone) {
      form.setError('timezone', { message: 'Choose a timezone or inherit it' })
      return
    }
    onPendingChange?.(true)
    try {
      const payload = toDisplayPayload(values)
      const saved = display ? await update.mutateAsync(payload) : await create.mutateAsync(payload)
      onSuccess(saved)
    } catch (error) {
      if (!applyServerErrors(error, form.setError, { fields: FIELDS })) notifyError(error)
    } finally {
      onPendingChange?.(false)
    }
  })

  return (
    <form id={formId} onSubmit={onSubmit} className="grid gap-5" noValidate>
      <FormError message={errors.root?.server?.message} />
      <Field label="Name" required error={errors.name?.message}>
        <Input placeholder="Lobby — left screen" autoFocus {...form.register('name')} />
      </Field>
      <Field label="Location" optional error={errors.location_id?.message} hint={locationOptions.length === 0 ? 'No locations yet — you can attach one later.' : undefined}>
        <Controller
          control={form.control}
          name="location_id"
          render={({ field }) => (
            <Combobox value={field.value} onChange={field.onChange} options={locationOptions} placeholder="No location" searchPlaceholder="Search locations…" allowClear />
          )}
        />
      </Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Orientation" error={errors.orientation?.message}>
          <Controller control={form.control} name="orientation" render={({ field }) => <OrientationSelect value={field.value} onChange={field.onChange} />} />
        </Field>
        {!compact && (
          <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
            <Field label="Resolution" optional error={errors.resolution_width?.message}>
              <Input inputMode="numeric" placeholder="1920" {...form.register('resolution_width')} />
            </Field>
            <span className="pb-2 text-muted-foreground">×</span>
            <Field label={<span className="sr-only">Height</span>} error={errors.resolution_height?.message}>
              <Input inputMode="numeric" placeholder="1080" aria-label="Resolution height" {...form.register('resolution_height')} />
            </Field>
          </div>
        )}
      </div>
      {!compact && (
        <>
          <Field label="Timezone" error={errors.timezone?.message}>
            <div>
              <InheritableTimezone
                inherit={inherit}
                onInheritChange={(v) => {
                  form.setValue('inherit_timezone', v)
                  if (!v && !form.getValues('timezone')) form.setValue('timezone', browserTimeZone())
                }}
                value={form.watch('timezone')}
                onChange={(v) => form.setValue('timezone', v, { shouldValidate: true })}
                inheritedLabel={location ? `From ${location.label} (${location.hint})` : display ? `Currently ${display.effective_timezone}` : 'From the location, or your organization'}
              />
            </div>
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Groups" optional error={errors.group_ids?.message}>
              <Controller
                control={form.control}
                name="group_ids"
                render={({ field }) => <MultiCombobox value={field.value} onChange={field.onChange} options={groupOptions} placeholder="No groups" emptyText="No groups yet" />}
              />
            </Field>
            <Field label="Tags" optional error={errors.tag_ids?.message}>
              <Controller
                control={form.control}
                name="tag_ids"
                render={({ field }) => <MultiCombobox value={field.value} onChange={field.onChange} options={tagOptions} placeholder="No tags" emptyText="No tags yet" />}
              />
            </Field>
          </div>
          <Field label="Description" optional error={errors.description?.message}>
            <Textarea rows={3} placeholder="Mounting position, screen model, anything useful for installers…" {...form.register('description')} />
          </Field>
        </>
      )}
      {!hideActions && (
        <div className="flex justify-end">
          <Button type="submit" loading={isSubmitting}>
            {submitLabel ?? (display ? 'Save changes' : 'Add display')}
          </Button>
        </div>
      )}
    </form>
  )
}
